import Vue from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import vuetify, { injectThemeVariables } from "./plugins/vuetify";
import Croppa from "vue-croppa";
import "vue-croppa/dist/vue-croppa.css";
import i18n from "./i18n";
import { initLocale } from "./utils/language-service";
import { createProvider } from "./vue-apollo";
import "./utils/fetch-interceptor";
import * as VeeValidate from "vee-validate";
import { extend } from "vee-validate";
import { required } from "vee-validate/dist/rules";
import { UserRoleSettings, AdminUserPermissions } from "./utils/user-role";
import { getCurrencySymbol } from "./utils/currency-helper";
import { locationLabel as getLocationLabel, useGeoLocations as getUseGeoLocations } from "./utils/location-labels";
import { hasFeature, limitOf } from "./utils/entitlements";
import { localizedField } from "./utils/localized-field";
import moment from "vue-moment";
import VueHtmlToPaper from "vue-html-to-paper";
import "./scss/main.scss";
import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

injectThemeVariables();

// i18n.js already picked the locale up from localStorage when it constructed the
// instance (module-scope i18n.t() callers depend on that). This mirrors it onto
// Vuetify's rtl flag and the document's dir/lang before the first paint.
initLocale();

// ApexCharts has its own translation table (toolbar: "Download SVG/PNG/CSV",
// zoom labels). `window.Apex` is the library's global-defaults hook and merges
// under every chart's own options, so one assignment covers all charts. Static
// per page-load is fine — a language switch reloads the page by design.
if (i18n.locale === "ar") {
  window.Apex = {
    chart: {
      locales: [require("apexcharts/dist/locales/ar.json")],
      defaultLocale: "ar",
    },
  };
}

library.add(fas);
Vue.component("font-awesome-icon", FontAwesomeIcon);
Vue.filter("truncate", function(text, stop, clamp) {
  return text.slice(0, stop) + (stop < text.length ? clamp || "..." : "");
});
const options = {
  name: "_blank",
  specs: ["fullscreen=yes", "titlebar=yes", "scrollbars=yes"],
  styles: [
    "https://maxcdn.bootstrapcdn.com/bootstrap/4.0.0/css/bootstrap.min.css",
    "https://unpkg.com/kidlat-css/css/kidlat.css",
    "https://cdnjs.cloudflare.com/ajax/libs/vuetify/2.4.5/vuetify.min.js",
    "https://cdnjs.cloudflare.com/ajax/libs/vuetify/2.4.5/vuetify.css",
    "https://cdnjs.cloudflare.com/ajax/libs/vuetify/2.4.5/vuetify.min.js",
    "https://cdnjs.cloudflare.com/ajax/libs/vuetify/2.4.5/vuetify.js",
    "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.2/css/all.min.css",
    "https://cdnjs.cloudflare.com/ajax/libs/vuetify/2.4.5/json/attributes.json",
    "https://cdnjs.cloudflare.com/ajax/libs/vuetify/2.4.5/json/tags.json",
    "https://cdnjs.cloudflare.com/ajax/libs/vuetify/2.4.5/json/web-types.json",
    "https://cdnjs.cloudflare.com/ajax/libs/vuetify/2.4.5/vuetify.css.map",
    "https://cdnjs.cloudflare.com/ajax/libs/vuetify/2.4.5/vuetify.js.map",
    "../styles/pdf.css",
    "../styles/meal_sticker.css",
  ],
};

Vue.use(VueHtmlToPaper, options);
Vue.config.productionTip = false;
Vue.use(VeeValidate);
//Vue.use(require('vue-moment'));
Vue.use(moment);
Vue.use(Croppa);
Vue.mixin({
  data: function() {
    return {
      AdminUserPermissions: AdminUserPermissions,
    };
  },
  methods: {
    capitalizeFirstLetter(str) {
      if (str) return str.charAt(0).toUpperCase() + str.slice(1);
      else return "";
    },
    formatCurrency(amount) {
      const currencySymbol = getCurrencySymbol();
      return `${parseFloat(amount).toFixed(2)} ${currencySymbol}`;
    },
    getCurrencySymbol() {
      return getCurrencySymbol();
    },
    hasRole(role) {
      return new UserRoleSettings().hasRole(role);
    },
    isNumber: function(evt) {
      evt = evt ? evt : window.event;
      var charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        evt.preventDefault();
      } else {
        return true;
      }
    },
    locationLabel(key) {
      return getLocationLabel(key);
    },
    /*
     * Render the locale-appropriate half of a bilingual data field:
     * `localized(item, 'title')` -> titleAr in Arabic, titleEn otherwise (and
     * titleEn as fallback when the Arabic column is blank). See
     * utils/localized-field.js.
     */
    localized(item, base) {
      return localizedField(item, base);
    },
    useGeoLocations() {
      return getUseGeoLocations();
    },
    /*
     * SaaS plan gates (utils/entitlements.js): is a plan feature included, and the plan's limit
     * for a key (null = unlimited). Both read "included / unlimited" until the SaaS backend sends
     * an entitlements snapshot, so they never hide anything for a tenant without one.
     */
    $hasFeature(key) {
      return hasFeature(key);
    },
    $planLimit(key) {
      return limitOf(key);
    },
    numberWithCommas(x) {
      if (!x) {
        return 0;
      }
      return parseFloat(x)
        .toFixed(3)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
  },
});
// A function message, not a string: vee-validate substitutes the field name
// itself via {_field_}, which vue-i18n would otherwise swallow as one of its own
// placeholders. Resolved per-validation, so it follows the active locale.
extend("required", {
  ...required,
  message: field => i18n.t("validation.isRequired", { field }),
});
new Vue({
  router,
  store,
  vuetify,
  i18n,
  apolloProvider: createProvider(),
  render: h => h(App),
  mounted() {
    // Check if icon fonts loaded correctly. Sometimes a cached index.html
    // references stale/deleted asset files after a new deployment, causing
    // the web fonts to return 404/fallback HTML and display as squares.
    if (document.fonts && document.fonts.load) {
      setTimeout(() => {
        document.fonts.load("1em \"Material Design Icons\"")
          .then((loaded) => {
            if (loaded.length === 0 || !document.fonts.check("1em \"Material Design Icons\"")) {
              console.warn("Material Design Icons failed to load. Triggering cache-busting reload.");
              triggerCacheBuster();
            }
          })
          .catch((err) => {
            console.error("Material Design Icons load error:", err);
            triggerCacheBuster();
          });
      }, 1500);
    }
  }
}).$mount("#app");

function triggerCacheBuster() {
  const url = new URL(window.location.href);
  // Avoid infinite loops by only reloading if we haven't cache-busted in the current query params
  if (!url.searchParams.has("t")) {
    url.searchParams.set("t", Date.now());
    window.location.replace(url.toString());
  }
}
